import { toast } from 'react-toastify'
import { Link, useNavigate } from 'react-router-dom'
import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  SelectorIcon,
  StatusOfflineIcon,
  StatusOnlineIcon
} from '@heroicons/react/solid'
import { PencilIcon, TrashIcon } from '@heroicons/react/outline'

import { Pagination } from 'components/Pagination'
import { PageHeader } from 'components/PageHeader'
import { LoadingCircle } from 'components/Loading'
import { SimpleButton } from 'components/FormInputs/Button'
import { ProtectedSection } from 'components/ProtectedSection'
import { RowActionsDropdown } from 'components/RowActionsDropdown'
import { SearchBar } from 'components/Search'
import { SortDownIcon } from 'components/Table/SortDownIcon'
import { SorUptIcon } from 'components/Table/SortUpIcon'
import { SortIcon } from 'components/Table/SortIcon'

import config from 'config'
import { Product, ProductType, Role } from 'models'
import { list, toggle, destroy } from '../services/api'
import { date, time } from 'utils'
import { translations } from '../translations'
import { useURLParams } from 'hooks/useURLParams'
import { ReadResult } from 'interfaces/queryOptions'

const productTypeOptions = [
  { value: '', label: 'Todos' },
  { value: ProductType.hardware, label: 'Hardware' },
  { value: ProductType.connectivity, label: 'Conectividade' },
  { value: ProductType.software, label: 'Software' }
]

export function List() {
  const navigate = useNavigate()
  const query = useURLParams()

  const queryParams = useMemo(() => {
    return { search: query.get('search') ?? '' }
  }, [query])

  const [page, setPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const [sort, setSort] = useState<Record<string, number>>({ created_at: -1 })
  const [sortedValue, setSortedValue] = useState<string>('created_at')
  const [isSortedDesc, setIsSortedDesc] = useState(false)
  const [search, setSearch] = useState(queryParams.search)
  const [typeFilter, setTypeFilter] = useState('')
  const [total, setTotal] = useState(0)
  const [data, setData] = useState<ReadResult<Product>[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const maxItemsPerPage = config.items_per_list_page

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      try {
        const options = {
          limit: maxItemsPerPage,
          search,
          page,
          sort,
          filters: typeFilter
            ? [{ key: 'type', op: 'eq', value: typeFilter }]
            : []
        }
        const { count, data } = await list(options)
        setData(data)
        setTotal(count)
      } catch (err) {
        toast.error('Falha ao buscar produtos')
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [maxItemsPerPage, page, search, sort, typeFilter])

  useEffect(() => {
    setLastPage(Math.ceil(total / maxItemsPerPage))
  }, [maxItemsPerPage, total])

  const onSort = useCallback(
    (sortBy: string) => {
      const sortValue = isSortedDesc ? -1 : 1
      setSort({ [sortBy]: sortValue })
      setSortedValue(sortBy)
      setIsSortedDesc(!isSortedDesc)
    },
    [isSortedDesc]
  )

  const onPageChange = useCallback(
    (page: number) => setPage(Math.min(Math.max(1, page), lastPage)),
    [lastPage]
  )

  const onSearch = useCallback(
    (term: string) => {
      if (!term || term.length < config.min_search_length) term = ''
      if (term === '') {
        query.delete('search')
      } else {
        query.set('search', term)
      }
      navigate(
        { pathname: window.location.pathname, search: query.toString() },
        { replace: true }
      )
      setSearch(term)
      setPage(1)
    },
    [query, navigate]
  )

  const onToggle = useCallback(
    (id: number, isActive: boolean) => {
      setData(
        data.map((product: any) => {
          if (product.id === id) {
            return {
              ...product,
              attributes: { ...product.attributes, active: isActive }
            }
          }
          return product
        })
      )
    },
    [data]
  )

  const doToggle = useCallback(
    async (productId: number, isActive: boolean) => {
      try {
        const { id, attributes } = await toggle(productId, isActive)
        toast.success(
          isActive
            ? 'Produto desativado com sucesso'
            : 'Produto ativado com sucesso'
        )
        onToggle(id, attributes.active ?? !isActive)
      } catch (err: any) {
        toast.error(
          err.suggestedMessage ?? 'Não foi possível alterar o produto'
        )
      }
    },
    [onToggle]
  )

  const doDestroy = useCallback(
    async (productId: number) => {
      try {
        await destroy(productId)
        toast.success('Produto excluído com sucesso')
        setData(data.filter((p) => p.id !== productId))
        setTotal(total - 1)
      } catch (err: any) {
        toast.error(
          err.suggestedMessage ?? 'Não foi possível excluir o produto'
        )
      }
    },
    [data, total]
  )

  const SortHeader = ({ label, field }: { label: string; field: string }) => (
    <th
      scope="col"
      className="group cursor-pointer px-4 py-3 text-left text-sm font-light text-gray-500"
      onClick={() => onSort(field)}
    >
      <div className="flex justify-between items-center">
        <div>{label}</div>
        {sortedValue === field ? (
          isSortedDesc ? (
            <SortDownIcon className="h-4 w-4" />
          ) : (
            <SorUptIcon className="h-4 w-4" />
          )
        ) : (
          <SortIcon className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100" />
        )}
      </div>
    </th>
  )

  const subtypeLabel = (product: ReadResult<Product>) => {
    const subtype = product.attributes.meta?.allowed_subtypes?.[0]
    if (!subtype) return '-'
    return translations.subtype[subtype] ?? subtype
  }

  return (
    <>
      <PageHeader title="Produtos" action="Listagem">
        <div className="flex flex-1 items-center gap-2">
          <SearchBar value={search} onChange={onSearch} />
          <div className="relative">
            <select
              className="appearance-none border border-gray-300 rounded-md text-sm text-gray-700 pl-3 pr-8 py-2 focus:outline-none"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value)
                setPage(1)
              }}
            >
              {productTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <SelectorIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
        <ProtectedSection roles={[Role.MANAGER]}>
          <Link to="/products/create">
            <SimpleButton>Novo</SimpleButton>
          </Link>
        </ProtectedSection>
      </PageHeader>

      {isLoading ? (
        <LoadingCircle />
      ) : (
        <div className="flex flex-col animate-fade-in-down">
          <div className="-my-2 overflow-x-auto px-4">
            <div className="py-2 align-middle inline-block min-w-full">
              <div className="shadow border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-sm font-light text-gray-500"
                      >
                        ATIVO
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-sm font-light text-gray-500"
                      >
                        CÓDIGO
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-sm font-light text-gray-500"
                      >
                        NOME
                      </th>
                      <SortHeader label="TIPO" field="type" />
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-sm font-light text-gray-500"
                      >
                        RESTRIÇÃO
                      </th>
                      <SortHeader label="CRIADO EM" field="created_at" />
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-sm font-light text-gray-500"
                      >
                        AÇÕES
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data.map((product, index) => (
                      <tr
                        key={product.id}
                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                          {product.attributes.active ? (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800">
                              Sim
                            </span>
                          ) : (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Não
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-left text-xs whitespace-nowrap text-gray-500">
                          {product.attributes.code ?? '-'}
                        </td>
                        <td className="px-4 py-3 text-left text-sm max-w-xs truncate whitespace-nowrap">
                          <Link
                            to={`/products/${product.id}/view`}
                            className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 hover:underline"
                          >
                            {product.attributes.name ?? '-'}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                          <div className="text-xs font-semibold text-gray-900">
                            {translations.type[
                              product.attributes.type as string
                            ] ?? '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                          <div className="text-xs text-gray-500">
                            {subtypeLabel(product)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-left text-sm whitespace-nowrap">
                          <div className="flex">
                            <div className="flex-1">
                              <div className="text-xs font-semibold text-gray-900">
                                {date(product.attributes.created_at)}
                              </div>
                              <div className="text-xs text-gray-500">
                                {time(product.attributes.created_at)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-left text-gray-500 max-w-sm">
                          <ProtectedSection roles={[Role.MANAGER]}>
                            <RowActionsDropdown
                              actions={[
                                {
                                  label: product.attributes.active
                                    ? 'Desativar'
                                    : 'Ativar',
                                  icon: product.attributes.active
                                    ? StatusOfflineIcon
                                    : StatusOnlineIcon,
                                  onClick: () =>
                                    doToggle(
                                      product.id,
                                      product.attributes.active ?? false
                                    )
                                },
                                {
                                  label: 'Editar',
                                  icon: PencilIcon,
                                  href: `/products/${product.id}`
                                },
                                {
                                  label: 'Excluir',
                                  icon: TrashIcon,
                                  onClick: () => doDestroy(product.id)
                                }
                              ]}
                            />
                          </ProtectedSection>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="mx-4">
            <Pagination
              currentPage={page}
              pageSize={maxItemsPerPage}
              itemsOnPage={data.length}
              totalItems={total}
              onPageChange={onPageChange}
            />
          </div>
        </div>
      )}
    </>
  )
}
